import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useRouteMatch} from 'react-router-dom'

import {Typography, Box, Chip} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as UsersIcon} from '@progyny/assets/icons/users.svg'
import {ConsultationType} from '@progyny/generated/graphql'
import OverviewCard from '@progyny/pages/Consultations/components/OverviewCard'
import {
  formatAppointmentDate,
  formatAppointmentTimeRange,
} from '@progyny/utils/appointmentHelpers'
import {getCancellationUrl} from '@progyny/utils/calcom'

interface OnboardingSectionProps {
  consultation: ConsultationType
  colors: any
  /** When true, always show the section so the user can book a 1-on-1 even when already enrolled in programs (e.g. Menopause). */
  alwaysShowBookingOption?: boolean
  onCancelRequested?: (cancelUrl: string) => void
}

const useStyles = makeStyles()(() => ({
  statusLabel: {
    padding: '6px 16px 4px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    display: 'inline-block',
    marginBottom: '8px',
    width: 'fit-content',
  },
  date: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '130%',
    color: '#251F1F',
    marginBottom: '4px',
  },
  time: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '130%',
    color: '#251F1F',
    marginBottom: '16px',
  },
}))

function getAppointmentStatusLabel(
  status: string | null | undefined,
  t: (key: string) => string
): string {
  if (!status) return t('consultations_onboarding_section_scheduled')

  switch (status.toUpperCase()) {
    case 'SCHEDULED':
    case 'RESCHEDULED':
      return t('consultations_program_session_upcoming')
    case 'CANCELLED':
      return t('consultations_onboarding_section_cancelled')
    case 'COMPLETED':
      return t('consultations_program_session_completed')
    case 'NO_SHOW':
      return t('consultations_program_session_not_attended')
    default:
      return t('consultations_onboarding_section_scheduled')
  }
}

function getAppointmentStatusColors(status: string | null | undefined): {
  backgroundColor: string
  textColor: string
} {
  if (!status) {
    return {
      backgroundColor: '#E7EDF9',
      textColor: '#07378F',
    }
  }

  switch (status.toUpperCase()) {
    case 'SCHEDULED':
    case 'RESCHEDULED':
      return {
        backgroundColor: '#E7EDF9',
        textColor: '#07378F',
      }
    case 'COMPLETED':
      return {
        backgroundColor: '#D1FAE5',
        textColor: '#065F46',
      }
    case 'CANCELLED':
    case 'NO_SHOW':
      return {
        backgroundColor: '#FFF3EC',
        textColor: '#E97E19',
      }
    default:
      return {
        backgroundColor: '#E7EDF9',
        textColor: '#07378F',
      }
  }
}

const OnboardingSection: React.FC<OnboardingSectionProps> = ({
  consultation,
  colors,
  alwaysShowBookingOption = false,
  onCancelRequested,
}) => {
  const {classes} = useStyles()
  const history = useHistory()
  const {url: rawUrl} = useRouteMatch()
  const {t} = useTranslation()

  // Remove trailing slash to prevent double slashes in URLs
  const url = rawUrl.endsWith('/') ? rawUrl.slice(0, -1) : rawUrl

  const isCallCompleted =
    consultation.myEnrollment?.onboardingStatus?.onboardingCallCompleted

  const onboardingAppointment =
    consultation.myEnrollment?.onboardingCallAppointment

  const isCallScheduled =
    !!onboardingAppointment && onboardingAppointment.status !== 'CANCELLED'

  const cancellationUrl = onboardingAppointment?.calcomBookingUid
    ? getCancellationUrl(onboardingAppointment?.calcomBookingUid)
    : null

  const isQuestionnaireCompleted =
    consultation.myEnrollment?.onboardingStatus
      ?.onboardingQuestionnaireCompleted

  // Hide section if programs are unlocked AND no outstanding appointment (unless alwaysShowBookingOption, e.g. Menopause)
  const hasOutstandingAppointment = isCallScheduled && !isCallCompleted
  if (
    !alwaysShowBookingOption &&
    consultation.canEnrollInPrograms &&
    !hasOutstandingAppointment
  ) {
    return null
  }

  const handleScheduleCallClick = () => {
    if (
      consultation.onboardingQuestionnaireRequired &&
      !isQuestionnaireCompleted
    ) {
      history.push(`${url}/assessment`)
    } else {
      history.push(`${url}/onboarding`)
    }
  }

  const handleCancelClick = () => {
    if (!cancellationUrl || !onCancelRequested) {
      return
    }
    onCancelRequested(cancellationUrl)
  }

  // If user has already scheduled a call.
  if (hasOutstandingAppointment) {
    const statusLabel = getAppointmentStatusLabel(
      onboardingAppointment.status,
      t
    )
    const statusColors = getAppointmentStatusColors(
      onboardingAppointment.status
    )

    return (
      <OverviewCard
        icon={UsersIcon}
        title={t('consultations_onboarding_section_your_onboarding_call')}
        content={
          <Box>
            {statusLabel && (
              <Chip
                label={statusLabel}
                size='medium'
                sx={{
                  marginBottom: 2,
                  marginTop: 1,
                  backgroundColor: statusColors.backgroundColor,
                  color: statusColors.textColor,
                }}
              />
            )}

            <Typography className={classes.date}>
              {formatAppointmentDate(onboardingAppointment.scheduledTime)}
            </Typography>
            <Typography className={classes.time}>
              {formatAppointmentTimeRange(
                onboardingAppointment.scheduledTime,
                30
              )}
            </Typography>
          </Box>
        }
        primaryButton={{
          label: t('consultations_onboarding_section_reschedule_call'),
          onClick: () => {
            history.push(`${url}/onboarding/reschedule`)
          },
          disabled: !onboardingAppointment.canReschedule,
        }}
        secondaryButton={
          onCancelRequested
            ? {
                label: t('common_cancel'),
                onClick: handleCancelClick,
                disabled: !cancellationUrl,
              }
            : undefined
        }
        colors={colors}
      />
    )
  }

  // Schedule a new call.
  return (
    <OverviewCard
      icon={UsersIcon}
      title={t('book_onboarding_call')}
      content={
        <div
          // This can't be fixed with i18n.
          dangerouslySetInnerHTML={{
            __html: t(consultation.onboardingDescription || ''),
          }}
        />
      }
      primaryButton={{
        label: t(consultation.onboardingButtonText || ''),
        onClick: handleScheduleCallClick,
      }}
      colors={colors}
      isFullRow
    />
  )
}

export default OnboardingSection
